import ThemedElement from "shared/components/ThemedElement";
import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
} from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import CancelIcon from "@mui/icons-material/Cancel";
import SpeedIcon from "@mui/icons-material/Speed";
import TimerIcon from "@mui/icons-material/Timer";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TuneIcon from "@mui/icons-material/Tune";

import { useFormLote } from "../../hooks/useFormEnvioLote";
import ConfirmacionLote from "./ConfirmacionImportacionLote";

const colors = {
  primary: "#0b4f6c",
  success: "#27ae60",
  error: "#e74c3c",
  info: "#2980b9",
  background: "#ffffffff",
  border: "#dfe6e9",
  warning: "#ed6c02",
};

const FormImportacionLote = ({ onImportSuccess, onError }) => {
  const {
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    resumenTipos,
    importStatus,
    processedRows,
    totalRows,
    isAsyncMode,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
  } = useFormLote();

  const [showErrorDetails, setShowErrorDetails] = React.useState(true);

  const onFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFileChange(file);
    }
  };

  const onImportClick = async () => {
    await handleImport(onImportSuccess, onError);
  };

  const handleDownloadTemplate = () => {
    try {
      const templateUrl = "/templates/lote_conciliacion.xlsx";
      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = "lote_conciliacion.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "Descargando plantilla de ejemplo...",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al descargar plantilla:", error);
      setSnackbar({
        open: true,
        message: "Error al descargar la plantilla. Contacte al administrador.",
        severity: "error",
      });
    }
  };

  const hasErrors =
    validationErrors.length > 0 || duplicateInFile.length > 0 || duplicateInDatabase.length > 0;
  const isValidFile = !!selectedFile && !hasErrors && !validating;
  const isLargeFile = selectedFile && selectedFile.size > 40 * 1024 * 1024;

  const getStatusInfo = () => {
    switch (importStatus) {
      case "uploading":
        return { label: "Subiendo archivo...", color: colors.info };
      case "processing":
        return { label: "Procesando registros...", color: colors.warning };
      case "completed":
        return { label: "Completado", color: colors.success };
      case "error":
        return { label: "Error", color: colors.error };
      default:
        return { label: "Listo", color: colors.primary };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: `2px solid ${colors.border}`,
          backgroundColor: "white",
          mb: 3,
        }}
      >
        <Box sx={{ backgroundColor: colors.primary, color: "white", p: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <CloudUploadIcon fontSize="large" />
              <Box>
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  Importar Lotes Retroactivos
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
                  Archivo Excel con datos de Código Paso, Fecha, Llave de Liquidación y Tipo (1 o 2)
                </MDTypography>
              </Box>
            </Box>

            <Box display="flex" gap={1} alignItems="center">
              {isAsyncMode && (
                <Tooltip title="Modo asíncrono activado para archivos grandes">
                  <Chip
                    icon={<SpeedIcon />}
                    label="Modo Asíncrono"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "& .MuiChip-icon": { color: "white" },
                    }}
                  />
                </Tooltip>
              )}

              <MDButton
                variant="outlined"
                onClick={handleDownloadTemplate}
                startIcon={<DownloadIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "white",
                  borderColor: "rgba(255,255,255,0.5)",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Descargar Plantilla
              </MDButton>
            </Box>
          </Box>
        </Box>

        <Box px={3} py={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `2px dashed ${
                    isValidFile
                      ? colors.success
                      : hasErrors && selectedFile
                      ? colors.error
                      : colors.border
                  }`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  backgroundColor: isValidFile
                    ? `${colors.success}08`
                    : hasErrors && selectedFile
                    ? `${colors.error}08`
                    : colors.background,
                  transition: "all 0.2s",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: isValidFile
                      ? colors.success
                      : hasErrors && selectedFile
                      ? colors.error
                      : colors.info,
                    backgroundColor: isValidFile
                      ? `${colors.success}10`
                      : hasErrors && selectedFile
                      ? `${colors.error}10`
                      : "#fff",
                  },
                }}
                onClick={() => {
                  document.getElementById("file-input")?.click();
                }}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onFileSelect}
                  style={{ display: "none" }}
                  disabled={validating || importing}
                />
                {validating ? (
                  <>
                    <CircularProgress size={48} sx={{ mb: 1 }} />
                    <MDTypography variant="h6" color={colors.info} gutterBottom>
                      Validando archivo...
                    </MDTypography>
                  </>
                ) : selectedFile ? (
                  <>
                    {isValidFile ? (
                      <CheckCircleIcon sx={{ fontSize: 48, color: colors.success, mb: 1 }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 48, color: colors.error, mb: 1 }} />
                    )}
                    <MDTypography
                      variant="h6"
                      color={isValidFile ? colors.success : colors.error}
                      gutterBottom
                    >
                      {isValidFile ? "Archivo válido" : "Archivo inválido"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {selectedFile.name}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      ({Math.round(selectedFile.size / 1024)} KB
                      {isLargeFile && (
                        <ThemedElement
                          component="span"
                          style={{ color: colors.warning, fontWeight: "bold" }}
                        >
                          {" "}
                          - Archivo grande (modo asíncrono)
                        </ThemedElement>
                      )}
                      )
                    </MDTypography>
                    <MDButton
                      variant="text"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelectedFile();
                      }}
                      sx={{ mt: 1 }}
                      disabled={importing}
                    >
                      Cambiar archivo
                    </MDButton>
                  </>
                ) : (
                  <>
                    <DescriptionIcon
                      sx={{
                        fontSize: 48,
                        color: validationErrors.length > 0 ? colors.error : colors.info,
                        mb: 1,
                      }}
                    />
                    <MDTypography
                      variant="h6"
                      color={validationErrors.length > 0 ? colors.error : colors.info}
                      gutterBottom
                    >
                      {validationErrors.length > 0
                        ? "Archivo inválido"
                        : "Seleccionar archivo Excel"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Haga clic para buscar un archivo .xlsx o .xls
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Columnas requeridas:{" "}
                      <strong>Codigo_Paso, Fecha_liquidacion, llave_liquidacion, Tipo</strong>
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      <ThemedElement component="span" style={{ color: colors.error }}>
                        *
                      </ThemedElement>{" "}
                      Todas las columnas son obligatorias y no pueden tener campos vacíos
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      <ThemedElement component="span" style={{ color: colors.error }}>
                        *
                      </ThemedElement>{" "}
                      <strong>llave_liquidacion</strong> debe ser numérica
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      <ThemedElement component="span" style={{ color: colors.error }}>
                        *
                      </ThemedElement>{" "}
                      <strong>Tipo</strong> debe ser <strong>1</strong> (conciliación diaria) o{" "}
                      <strong>2</strong> (ajustes)
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      <TimerIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.5 }} />
                      Archivos grandes (&gt;40MB) se procesan en modo asíncrono
                    </MDTypography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Resumen de tipos detectados */}
          {selectedFile && isValidFile && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "#f8fafc",
                border: `1px solid ${colors.border}`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <TuneIcon sx={{ fontSize: 20, color: colors.primary }} />
                <MDTypography variant="subtitle2" fontWeight="bold" color={colors.primary}>
                  Tipos de conciliación detectados en el archivo
                </MDTypography>
              </Box>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <Chip
                  icon={<EventNoteIcon sx={{ fontSize: 16 }} />}
                  label={`Tipo 1 — Conciliación diaria: ${resumenTipos?.tipo1 || 0}`}
                  sx={{
                    bgcolor: "#e8f5e9",
                    color: "#2e7d32",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    border: "1px solid #a5d6a7",
                    "& .MuiChip-icon": { color: "#2e7d32" },
                  }}
                />
                <Chip
                  icon={<TuneIcon sx={{ fontSize: 16 }} />}
                  label={`Tipo 2 — Ajustes: ${resumenTipos?.tipo2 || 0}`}
                  sx={{
                    bgcolor: "#fff3e0",
                    color: "#e65100",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    border: "1px solid #ffcc80",
                    "& .MuiChip-icon": { color: "#e65100" },
                  }}
                />
              </Box>
              <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Los registros se procesarán según el tipo indicado en cada fila.
              </MDTypography>
            </Box>
          )}

          {/* Estado de la importación */}
          {importing && importStatus !== "idle" && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} sx={{ color: statusInfo.color }} />
                  <MDTypography variant="body2" fontWeight="bold" color={statusInfo.color}>
                    {statusInfo.label}
                  </MDTypography>
                </Box>
                <MDTypography variant="caption" color="text.secondary">
                  {Math.round(importProgress)}%
                </MDTypography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={importProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: `${statusInfo.color}30`,
                  "& .MuiLinearProgress-bar": { bgcolor: statusInfo.color },
                }}
              />
              {importStatus === "processing" && processedRows > 0 && totalRows > 0 && (
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  Procesados: {processedRows.toLocaleString()} de {totalRows.toLocaleString()}{" "}
                  registros
                  {isAsyncMode && " (modo asíncrono)"}
                </MDTypography>
              )}
            </Box>
          )}

          {/* Errores de validación */}
          {validationErrors.length > 0 && selectedFile && (
            <Collapse in={showErrorDetails}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#ffebee",
                  borderRadius: 2,
                  border: "1px solid #ef9a9a",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#c62828",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorIcon />
                    <MDTypography variant="subtitle2" fontWeight="bold" color="white">
                      Errores de validación ({validationErrors.length})
                    </MDTypography>
                    <MDButton
                      size="small"
                      variant="text"
                      sx={{ color: "white", ml: 2 }}
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                    >
                      {showErrorDetails ? "Ocultar" : "Mostrar"}
                    </MDButton>
                  </Box>
                </Box>
                <Box sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
                  <List dense disablePadding>
                    {validationErrors.slice(0, 10).map((err, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CancelIcon sx={{ fontSize: 16, color: colors.error }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontFamily: "monospace" },
                          }}
                        />
                      </ListItem>
                    ))}
                    {validationErrors.length > 10 && (
                      <ListItem>
                        <ListItemText
                          primary={`... y ${validationErrors.length - 10} errores más`}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontStyle: "italic" },
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Duplicados en archivo */}
          {duplicateInFile.length > 0 && selectedFile && (
            <Collapse in={showErrorDetails}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#fff3e0",
                  borderRadius: 2,
                  border: "1px solid #ffe082",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#ed6c02",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <WarningIcon />
                  <MDTypography variant="subtitle2" fontWeight="bold" color="white">
                    Códigos Paso duplicados dentro del archivo ({duplicateInFile.length})
                  </MDTypography>
                </Box>
                <Box sx={{ p: 2, maxHeight: 200, overflow: "auto" }}>
                  <List dense disablePadding>
                    {duplicateInFile.map((dup, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <WarningIcon sx={{ fontSize: 16, color: "#ed6c02" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Fila ${dup.row}: Código "${dup.value}" duplicado`}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#ed6c02", fontFamily: "monospace" },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Duplicados en BD */}
          {duplicateInDatabase.length > 0 && selectedFile && (
            <Collapse in={showErrorDetails}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#ffebee",
                  borderRadius: 2,
                  border: "1px solid #ef9a9a",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#c62828",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ErrorIcon />
                  <MDTypography variant="subtitle2" fontWeight="bold" color="white">
                    Códigos Paso ya existentes en el sistema ({duplicateInDatabase.length})
                  </MDTypography>
                </Box>
                <Box sx={{ p: 2, maxHeight: 200, overflow: "auto" }}>
                  <List dense disablePadding>
                    {duplicateInDatabase.map((dup, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <ErrorIcon sx={{ fontSize: 16, color: "#c62828" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Código "${dup.value}" ya existe en el sistema`}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontFamily: "monospace" },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Archivo válido */}
          {isValidFile && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 2,
                border: "1px solid #a5d6a7",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleIcon sx={{ color: colors.success }} />
                <MDTypography variant="subtitle2" fontWeight="bold" color={colors.success}>
                  Validación exitosa
                </MDTypography>
                {isLargeFile && (
                  <Chip
                    size="small"
                    label="Modo Asíncrono"
                    icon={<SpeedIcon />}
                    sx={{ ml: 1, bgcolor: colors.info, color: "white" }}
                  />
                )}
              </Box>
              <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                El archivo está listo para ser importado.
                {isLargeFile && " Se procesará en modo asíncrono debido a su tamaño."}
              </MDTypography>
            </Box>
          )}

          {/* Instrucciones */}
          <Box
            sx={{
              mt: 4,
              mb: 2,
              p: 2.5,
              bgcolor: "#fff8e1",
              borderRadius: 2,
              border: "1px solid #ffe082",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HelpOutlineIcon sx={{ fontSize: 22, color: colors.warning }} />
              <MDTypography variant="subtitle1" fontWeight="bold" color={colors.warning}>
                Instrucciones para la importación
              </MDTypography>
            </Box>

            <MDTypography variant="body2" color="text.secondary" component="div">
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                <li>El archivo debe ser tipo Excel (.xlsx o .xls)</li>
                <li>
                  Debe contener EXACTAMENTE las columnas:{" "}
                  <strong>Codigo_Paso, Fecha_liquidacion, llave_liquidacion, Tipo</strong>
                </li>
                <li>La primera fila debe contener los encabezados</li>
                <li>
                  La columna <strong>Codigo_Paso</strong> es{" "}
                  <ThemedElement component="strong" style={{ color: colors.error }}>
                    OBLIGATORIA
                  </ThemedElement>{" "}
                  y debe ser única
                </li>
                <li>
                  <strong>Todas las columnas deben tener información</strong> - No se permiten
                  campos vacíos
                </li>
                <li>
                  <strong>llave_liquidacion</strong> debe ser un valor <strong>numérico</strong>
                </li>
                <li>
                  <strong>Tipo</strong> debe ser <strong>1</strong> (conciliación diaria) o{" "}
                  <strong>2</strong> (ajustes)
                </li>
                <li>
                  <strong>No se permiten códigos duplicados</strong> dentro del mismo archivo
                </li>
                <li>
                  <strong>Archivos grandes (&gt;40MB):</strong> Se procesan en modo asíncrono con
                  monitoreo de progreso
                </li>
                <li>
                  <strong>Recomendación:</strong> Descargue la plantilla de ejemplo para asegurar el
                  formato correcto
                </li>
              </ul>
            </MDTypography>

            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: "1px dashed #ffe082",
              }}
            >
              <MDTypography variant="caption" color="text.secondary">
                <strong>Ejemplo de fila válida:</strong>{" "}
                <code
                  style={{
                    backgroundColor: "#fff",
                    padding: "2px 6px",
                    borderRadius: 4,
                  }}
                >
                  10167_111002753526, 16/07/2026, 1607202601, 1
                </code>
              </MDTypography>
            </Box>
          </Box>

          {/* Botón Procesar */}
          <Box sx={{ mt: 3 }}>
            <MDButton
              variant="contained"
              fullWidth
              disabled={!isValidFile || importing || validating}
              onClick={onImportClick}
              size="large"
              sx={{
                py: 1.5,
                fontSize: "1rem",
                color: "white",
                fontWeight: "bold",
                borderRadius: 1,
                bgcolor: isValidFile ? colors.success : colors.border,
                "&:hover": {
                  bgcolor: isValidFile ? "#1e8449" : colors.border,
                },
                "&:disabled": {
                  bgcolor: colors.border,
                  color: "white",
                },
              }}
            >
              {importing ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1.5, color: "white" }} />
                  <MDTypography
                    variant="body1"
                    color="white"
                    sx={{ display: "inline", fontWeight: "bold" }}
                  >
                    {importStatus === "uploading"
                      ? "Subiendo archivo..."
                      : importStatus === "processing"
                      ? "Procesando registros..."
                      : "Procesando..."}
                  </MDTypography>
                </>
              ) : validating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1.5, color: "white" }} />
                  <MDTypography
                    variant="body1"
                    sx={{
                      display: "inline",
                      fontWeight: "bold",
                      color: colors.background,
                    }}
                  >
                    Validando...
                  </MDTypography>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ mr: 1.5, color: colors.background }} />
                  <MDTypography variant="body1" sx={{ color: colors.background }}>
                    {isLargeFile ? "Procesar Archivo (Asíncrono)" : "Procesar Registros"}
                  </MDTypography>
                </>
              )}
            </MDButton>
          </Box>
        </Box>
      </Paper>

      <ConfirmacionLote
        open={showConfirmModal}
        onClose={handleCloseModal}
        importing={importing}
        importResult={importResult}
        fileName={selectedFile?.name}
        importProgress={importProgress}
        lote={null}
        isAsyncMode={isAsyncMode}
        importStatus={importStatus}
        processedRows={processedRows}
        totalRows={totalRows}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "info" ? 5000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{
            width: "100%",
            minWidth: "300px",
            backgroundColor:
              snackbar.severity === "success"
                ? "#f0fff4"
                : snackbar.severity === "error"
                ? "#fff5f5"
                : snackbar.severity === "info"
                ? "#e3f2fd"
                : "#fff8e1",
            color:
              snackbar.severity === "success"
                ? "#276749"
                : snackbar.severity === "error"
                ? "#c53030"
                : snackbar.severity === "info"
                ? "#0b4f6c"
                : "#856404",
            "& .MuiAlert-icon": {
              color:
                snackbar.severity === "success"
                  ? "#276749"
                  : snackbar.severity === "error"
                  ? "#c53030"
                  : snackbar.severity === "info"
                  ? "#0b4f6c"
                  : "#856404",
            },
            border:
              snackbar.severity === "success"
                ? "1px solid #c6f6d5"
                : snackbar.severity === "error"
                ? "1px solid #fed7d7"
                : snackbar.severity === "info"
                ? "1px solid #b3e5fc"
                : "1px solid #ffe082",
          }}
        >
          <MDTypography variant="body1" fontWeight="bold">
            {snackbar.severity === "success"
              ? "¡Éxito!"
              : snackbar.severity === "error"
              ? "Error"
              : snackbar.severity === "info"
              ? "Información"
              : "Aviso"}
          </MDTypography>
          <MDTypography variant="body2">{snackbar.message}</MDTypography>
          {snackbar.severity === "info" && importProgress > 0 && importProgress < 100 && (
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

FormImportacionLote.propTypes = {
  onImportSuccess: PropTypes.func,
  onError: PropTypes.func,
};

FormImportacionLote.defaultProps = {
  onImportSuccess: null,
  onError: null,
};

export default FormImportacionLote;
